import React, { useEffect, useState, useMemo, useRef } from "react";
import Sidebar, { SidebarProvider } from "../../components/navigation/Sidebar";
import { supabase } from "../../lib/supabase";
import { connectFacebook } from "../../services/facebookService";
import { getFacebookPagePictureUrl } from "../../services/facebookPostsService";
import { useModal } from "../../components/ui/ModalProvider";
import { useLanguageProfile } from "../../contexts/LanguageProfileContext";
import { useRequestGuard } from "../../hooks/useRequestGuard";

const formatNumber = (num) => {
  if (!num && num !== 0) return "—";
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + "M";
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + "k";
  }
  return num.toString();
};

const formatDate = (value) => {
  if (!value) return "—";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "—" : parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
};

const formatFollowers = (count) => {
  if (!count && count !== 0) return null;
  if (count >= 1000) {
    return (count / 1000).toFixed(1) + "k";
  }
  return count.toString();
};

const isPageTokenExpired = (page) => {
  if (page?.page_access_token_is_valid === false) {
    return true;
  }
  if (!page?.page_access_token_expires_at) {
    return false;
  }
  const expires = new Date(page.page_access_token_expires_at).getTime();
  if (Number.isNaN(expires)) return false;
  return expires <= Date.now();
};

const COLORS = ["#1877F2", "#E60023", "#FF9500", "#30D158", "#FF2D55", "#AF52DE"];

export default function PageStats() {
  const { alert, confirm } = useModal();
  const { activeProfile } = useLanguageProfile();
  const startPageStatsRequest = useRequestGuard();
  const startInsightsRequest = useRequestGuard();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [needsReconnect, setNeedsReconnect] = useState(false);
  const [userId, setUserId] = useState(null);
  const [selectedPageId, setSelectedPageId] = useState(null);
  const [selectingPageId, setSelectingPageId] = useState(null);
  const [statsByPage, setStatsByPage] = useState({});
  const [fetchingStatsId, setFetchingStatsId] = useState(null);
  const [statsError, setStatsError] = useState("");
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [followerCounts, setFollowerCounts] = useState({});
  const [loadingFollowers, setLoadingFollowers] = useState(false);
  const [removingPageId, setRemovingPageId] = useState(null);
  const insightsRef = useRef(null);
  const [session, setSession] = useState(null);

  // Clear the previous profile's pages immediately on switch so stale
  // content can't flash while the new profile's data is loading.
  useEffect(() => {
    startInsightsRequest();
    setPages([]);
    setFollowerCounts({});
    setSelectedPageId(null);
    setStatsByPage({});
    setStatsError("");
    setInsightsLoading(false);
    setFetchingStatsId(null);
    return () => { startInsightsRequest(); };
  }, [activeProfile?.id, startInsightsRequest]);

  useEffect(() => {
    const isCurrent = startPageStatsRequest();

    async function init() {
      setLoading(true);
      const {
        data: { session: authSession },
      } = await supabase.auth.getSession();

      if (!isCurrent()) return;
      setSession(authSession);

      if (!authSession?.user) {
        setLoading(false);
        return;
      }

      setUserId(authSession.user.id);
      const pageList = await loadPages(authSession.user.id, activeProfile?.id, isCurrent);
      await loadSelection(authSession.user.id, isCurrent, pageList || []);
      if (isCurrent()) setLoading(false);
    }

    init();
  }, [activeProfile?.id, startPageStatsRequest]);

  async function loadPages(uid, profileId, isCurrent = () => true) {
    try {
      let query = supabase
        .from("facebook_pages")
        .select(
          "page_id,page_name,page_category,page_picture_url,page_access_token_expires_at,page_access_token_is_valid,fan_count,created_at,profile_id"
        )
        .eq("user_id", uid);
      if (profileId) query = query.eq("profile_id", profileId);
      const { data, error } = await query.order("created_at", { ascending: false });

      if (!isCurrent()) return;

      if (error) {
        console.error("Failed to load pages:", error);
        setConnected(false);
        setNeedsReconnect(true);
        setPages([]);
        return;
      }

      const pageList = data || [];
      const hasPages = pageList.length > 0;
      const hasValidTokens = hasPages && pageList.some((page) => !isPageTokenExpired(page));

      setPages(pageList);
      setConnected(hasPages);
      setNeedsReconnect(hasPages && !hasValidTokens);

      // Fetch follower counts for each page
      if (hasPages) {
        await fetchFollowerCounts(pageList, isCurrent);
      }
      return pageList;
    } catch (err) {
      if (!isCurrent()) return;
      console.error("Failed to load pages:", err);
      setConnected(false);
      setNeedsReconnect(true);
      setPages([]);
    }
  }

  async function fetchFollowerCounts(pageList, isCurrent = () => true) {
    setLoadingFollowers(true);
    // fan_count is Page likes, not followers. Populate followers from the live stats response.
    const counts = {};

    if (!isCurrent()) return;
    setFollowerCounts(counts);
    setLoadingFollowers(false);
  }

  async function loadSelection(uid, isCurrent = () => true, pageList = []) {
    const { data, error } = await supabase
      .from("facebook_page_selections")
      .select("page_id")
      .eq("user_id", uid)
      .order("created_at", { ascending: false })
      .limit(1);

    if (!isCurrent()) return;

    if (error) {
      console.error("Failed to load selected page:", error);
      return;
    }

    if (data?.length && pageList.some(page => page.page_id === data[0].page_id)) {
      setSelectedPageId(data[0].page_id);
    }
  }

  async function handleViewInsights(page) {
    const isCurrent = startInsightsRequest();
    setSelectedPageId(page.page_id);
    setStatsError("");
    setStatsByPage({});

    // Scroll to insights section
    setTimeout(() => {
      if (insightsRef.current) {
        insightsRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);

    // Fetch insights
    setFetchingStatsId(page.page_id);
    setInsightsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke("facebook-page-stats", {
        body: { page_id: page.page_id, profile_id: activeProfile?.id },
      });

      if (!isCurrent()) return;
      if (error) {
        const failure = await error.context?.json?.().catch(() => null);
        throw new Error(failure?.error || error.message || "Page Stats request failed");
      }

      const payload =
        typeof data === "string" ? JSON.parse(data || "{}") : data || {};

      if (!isCurrent()) return;
      if (payload.error) throw new Error(payload.error);
      if (String(payload.page?.id) !== String(page.page_id) || !payload.stats) {
        throw new Error("Page Stats returned an unexpected Page or response. Deploy the updated facebook-page-stats function.");
      }
      setFollowerCounts(prev => ({ ...prev, [page.page_id]: payload.page.followers_count ?? null }));
      setStatsByPage((prev) => ({
        ...prev,
        [page.page_id]: payload,
      }));
    } catch (err) {
      if (!isCurrent()) return;
      setStatsError("Could not load insights: " + (err.message || "Unknown error"));
    } finally {
      if (isCurrent()) {
        setFetchingStatsId(null);
        setInsightsLoading(false);
      }
    }
  }

  async function refreshPages() {
    if (!userId || !activeProfile?.id) return;
    const isCurrent = startPageStatsRequest();
    const profileId = activeProfile.id;
    setLoading(true);
    try {
      const { error } = await supabase.functions.invoke("sync-platform-insights", {
        body: { profileId, platform: "facebook" },
      });
      if (error) throw error;
    } catch (err) {
      console.error("Failed to refresh Facebook page data:", err);
    } finally {
      await loadPages(userId, profileId, isCurrent);
      if (isCurrent()) setLoading(false);
    }
  }

  async function handleRemovePage(pageId) {
    if (removingPageId) return; // prevent double-clicks / overlapping requests

    const ok = await confirm(
      "Are you sure you want to disconnect this account? All scheduled and automated publishing for this account will stop."
    );
    if (!ok) return;

    setRemovingPageId(pageId);

    try {
      const { data, error } = await supabase.functions.invoke("facebook-pages", {
        method: "DELETE",
        body: { page_id: pageId },
      });

      if (error) {
        throw new Error(error.message || "Failed to remove page");
      }
      if (data?.error) {
        throw new Error(typeof data.error === "string" ? data.error : data.error.message || "Failed to remove page");
      }

      // Card is only removed from the UI after the backend confirms deletion.
      setPages((prev) => prev.filter((p) => p.page_id !== pageId));
      if (selectedPageId === pageId) {
        startInsightsRequest();
        setSelectedPageId(null);
        setStatsByPage({});
        setStatsError("");
        setFetchingStatsId(null);
        setInsightsLoading(false);
      }
      await alert("Account disconnected successfully.", { tone: "success" });
    } catch (err) {
      console.error("Failed to remove Facebook page:", err);
      await alert(`Failed to remove page: ${err.message || "Please try again."}`);
    } finally {
      setRemovingPageId(null);
    }
  }

  const connectLabel = needsReconnect
    ? "Reconnect Facebook"
    : "Connect with Facebook";

  const selectedPage = pages.find((p) => p.page_id === selectedPageId);
  const selectedStats = selectedPageId ? statsByPage[selectedPageId] : null;

  return (
    <SidebarProvider>
      <div className="flex min-h-screen bg-gray-50">
        <Sidebar />

        <main className="flex-1 p-8">
          {/* Header */}
          <div className="flex flex-col gap-4 mb-8 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-800">
                Facebook Pages
              </h1>
              {needsReconnect && pages.length > 0 && (
                <p className="text-sm text-orange-600 mt-1">
                  Some pages may need reconnection. Click "Refresh Pages" to update.
                </p>
              )}
            </div>

            <div className="flex gap-3">
              {!connected && (
                <button
                  onClick={() => connectFacebook(activeProfile?.id)}
                  className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700"
                >
                  {connectLabel}
                </button>
              )}

              {connected && (
                <>
                  <button
                    onClick={() => connectFacebook(activeProfile?.id)}
                    className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700"
                  >
                    + Add Page
                  </button>

                  <button
                    onClick={refreshPages}
                    disabled={loading}
                    className="px-4 py-2 rounded-lg border bg-white text-sm disabled:opacity-50"
                  >
                    Refresh Pages
                  </button>
                </>
              )}
            </div>
          </div>

          {statsError && (
            <p className="text-sm text-rose-600 mb-4">{statsError}</p>
          )}

          {/* Content */}
          {loading ? (
            <div className="text-center text-gray-500 mt-20">
              Loading Facebook pages…
            </div>
          ) : pages.length === 0 ? (
            <EmptyState
              onConnect={() => connectFacebook(activeProfile?.id)}
              reconnect={needsReconnect}
            />
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {pages.map((page) => (
                  <PageCard
                    key={page.id ?? page.page_id}
                    page={page}
                    followerCount={followerCounts[page.page_id]}
                    loadingFollowers={loadingFollowers}
                    onViewInsights={handleViewInsights}
                    onRemove={handleRemovePage}
                    isSelected={selectedPageId === page.page_id}
                    isLoading={fetchingStatsId === page.page_id}
                    isRemoving={removingPageId === page.page_id}
                  />
                ))}
              </div>

              {/* Insights Section */}
              <div ref={insightsRef} className="mt-12">
                {!selectedPageId ? (
                  <div className="text-center py-12 text-gray-500 bg-white rounded-xl shadow-sm">
                    Select a page above to view its insights
                  </div>
                ) : insightsLoading ? (
                  <div role="status" aria-label="Loading Page insights" className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                  </div>
                ) : selectedStats ? (
                  <InsightsSection
                    key={selectedPageId}
                    page={{
                      ...selectedPage,
                      page_name: selectedStats.page?.name || selectedPage?.page_name,
                      page_category: selectedStats.page?.category || selectedPage?.page_category,
                    }}
                    followerCount={followerCounts[selectedPageId]}
                    stats={selectedStats}
                  />
                ) : (
                  <p className="text-sm text-gray-500">{statsError ? "Page data is unavailable. See the error above and retry View Insights." : "Click View Insights to load this Page’s data."}</p>
                )}
              </div>
            </>
          )}
        </main>
      </div>
    </SidebarProvider>
  );
}

/* ------------------------------
   Page Card
-------------------------------- */
function PageCard({
  page,
  followerCount,
  loadingFollowers,
  onViewInsights,
  onRemove,
  isSelected,
  isLoading,
  isRemoving,
}) {
  const [imgFailed, setImgFailed] = useState(false);
  const imageUrl = getFacebookPagePictureUrl(page.page_id) || page.page_picture_url;
  const statusExpired = isPageTokenExpired(page);
  const statusColor = statusExpired ? "text-rose-600" : "text-emerald-600";
  const statusLabel = statusExpired ? "Disconnected" : "Connected";
  const displayFollowers = followerCount !== undefined ? formatFollowers(followerCount) : (loadingFollowers ? null : null);

  return (
    <div
      className={`bg-white rounded-xl shadow-sm overflow-hidden border border-transparent transition-shadow duration-200 hover:shadow-md ${
        isSelected ? "border-indigo-500 ring-2 ring-indigo-300" : ""
      }`}
    >
      {/* Cover */}
      <div className="h-32 bg-gradient-to-r from-indigo-100 to-purple-100" />

      {/* Body */}
      <div className="relative px-6 pb-6 text-center">
        {isSelected && (
          <span className="absolute top-4 right-4 px-2 py-1 text-xs font-medium text-indigo-700 bg-indigo-100 rounded-full">
            Selected
          </span>
        )}

        {/* Avatar */}
        <div className="w-20 h-20 rounded-full border-4 border-white mx-auto -mt-10 bg-indigo-600 flex items-center justify-center text-white text-2xl font-bold overflow-hidden">
          {imageUrl && !imgFailed ? (
            <img
              src={imageUrl}
              alt={`${page.page_name} profile`}
              className="w-full h-full object-cover"
              onError={() => setImgFailed(true)}
            />
          ) : (
            <span>{page.page_name?.charAt(0)}</span>
          )}
        </div>

        <h3 className="mt-3 font-semibold text-lg text-gray-800">
          {page.page_name}
        </h3>

        <p className="text-xs text-gray-500 mt-1">ID: {page.page_id}</p>

        <p className="text-sm text-gray-500 mt-1">
          {page.page_category || "Facebook Page"}
        </p>

        {/* Follower Count - Prominent */}
        {displayFollowers ? (
          <p className="mt-3 text-xl font-semibold text-gray-700">
            {displayFollowers} Followers
          </p>
        ) : loadingFollowers ? (
          <div className="mt-3 flex items-center justify-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
          </div>
        ) : null}

        {/* Status - Centered */}
        <div className="mt-2">
          <p className={`text-xs font-semibold ${statusColor} text-center`}>
            Status: {statusLabel}
          </p>
        </div>

        {/* Actions - Full width row, evenly spaced */}
        <div className="flex flex-col gap-2 mt-5 w-full">
          <button
            onClick={() => onViewInsights(page)}
            disabled={isLoading}
            className="w-full px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {isLoading ? "Loading…" : "View Insights"}
          </button>

          <div className="flex gap-2 w-full">
            <a
              href={`https://facebook.com/${page.page_id}`}
              target="_blank"
              rel="noreferrer"
              className="flex-1 px-4 py-2 text-sm border rounded-lg hover:bg-gray-50 text-center"
            >
              Visit
            </a>

            {onRemove && (
              <button
                onClick={() => onRemove(page.page_id)}
                disabled={isRemoving}
                className="flex-1 px-4 py-2 text-sm text-red-500 hover:text-red-700 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isRemoving ? "Removing…" : "Remove"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------
   Insights Section
-------------------------------- */
function InsightsSection({ page, followerCount, stats }) {
  const [imgFailed, setImgFailed] = useState(false);
  const imageUrl = getFacebookPagePictureUrl(page.page_id) || page.page_picture_url;
  const values = stats?.stats || {};
  const states = stats?.metric_states || {};
  const statsData = [
    { icon: "📝", label: "Posts / Day (30d)", key: "posts_per_day" },
    { icon: "👁", label: "Media Views (7d)", key: "media_views_7d" },
    { icon: "📡", label: "Unique Media Viewers (7d)", key: "unique_viewers_7d" },
    { icon: "❤️", label: "Engagements (7d)", key: "engagements_7d" },
    { icon: "🖥", label: "Page Profile Views (7d)", key: "page_views_7d" },
    { icon: "📅", label: "Posts (30d)", key: "total_posts_30d" },
  ];

  const recentPosts = stats?.recent_posts?.slice(0, 5) || [];

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center gap-4">
          {imageUrl && !imgFailed ? (
            <img
              src={imageUrl}
              alt={page.page_name}
              className="w-16 h-16 rounded-full object-cover"
              onError={() => setImgFailed(true)}
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-indigo-600 flex items-center justify-center text-white text-2xl font-bold">
              {page.page_name?.charAt(0)}
            </div>
          )}
          <div>
            <h2 className="text-xl font-bold text-gray-800">{page.page_name}</h2>
            <span className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full mt-1">
              {page.page_category || "Facebook Page"}
            </span>
            <p className="text-lg font-semibold text-gray-700 mt-1">
              {followerCount == null ? "Followers unavailable" : `${formatFollowers(followerCount)} Followers`}
            </p>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="p-6 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">📊 Page Insights — {page.page_name}</h3>
        <p className="text-xs text-gray-500 mb-3">Insights use Meta’s latest complete 7-day periods. Post counts cover the last 30 days.</p>
        {[...new Set(stats.warnings || [])].map(message => <p key={message} role="alert" className="text-sm text-orange-700 mb-2">{message}</p>)}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {statsData.map((stat, idx) => (
            <div
              key={idx}
              className="bg-gray-50 rounded-lg p-4 shadow-sm"
            >
              <p className="text-2xl mb-1">{stat.icon}</p>
              <p className="text-xs text-gray-500">{stat.label}</p>
              <p className="text-xl font-bold text-gray-800">{values[stat.key] == null ? (states[stat.key]?.status === "error" ? "Error" : "Unavailable") : formatNumber(values[stat.key])}</p>
              {states[stat.key]?.message && <p className="text-xs text-gray-500 mt-1">{states[stat.key].message}</p>}
            </div>
          ))}
        </div>
      </div>

      {/* Recent Posts */}
      <div className="p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Recent Posts (30d)</h3>
        {stats.posts_state?.status !== "available" && <p role="alert" className="text-sm text-orange-700 mb-3">{stats.posts_state?.message || "Recent posts are unavailable."}</p>}
        {recentPosts.length > 0 ? (
          <div className="space-y-3">
            {recentPosts.map((post, idx) => (
              <div
                key={idx}
                className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                {/* Thumbnail */}
                <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                  {post.full_picture ? (
                    <img
                      src={post.full_picture}
                      alt="Post"
                      className="w-full h-full object-cover rounded-lg"
                    />
                  ) : (
                    <span className="text-xl">📝</span>
                  )}
                </div>

                {/* Message & Date */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-800 truncate">
                    {post.message?.substring(0, 80) || "No message"}
                    {post.message?.length > 80 && "..."}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {formatDate(post.created_time)}
                  </p>
                </div>

                {/* Engagement */}
                <div title={post.engagement_error || ""} className="flex items-center gap-3 text-xs text-gray-600">
                  <span>👍 {post.likes == null ? "Unavailable" : formatNumber(post.likes)}</span>
                  <span>💬 {post.comments == null ? "Unavailable" : formatNumber(post.comments)}</span>
                  <span>🔁 {post.shares == null ? "Unavailable" : formatNumber(post.shares)}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">{stats.posts_state?.status === "available" ? "No posts in the last 30 days." : "Recent posts are unavailable."}</p>
        )}
      </div>
    </div>
  );
}

/* ------------------------------
   Empty State
-------------------------------- */
function EmptyState({ onConnect, reconnect }) {
  const label = reconnect ? "Reconnect Facebook" : "Connect with Facebook";

  return (
    <div className="flex items-center justify-center h-[60vh]">
      <div className="bg-white rounded-xl shadow-sm p-10 text-center max-w-sm">
        <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-indigo-100 flex items-center justify-center text-xl">
          📊
        </div>

        <h2 className="text-lg font-semibold text-gray-800">
          Facebook Page Stats
        </h2>

        <p className="text-sm text-gray-500 mt-2">
          Connect your Facebook account to manage pages and view insights.
        </p>

        <div className="mt-6">
          <button
            onClick={onConnect}
            className="px-5 py-2 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 text-white"
          >
            {label}
          </button>
        </div>
      </div>
    </div>
  );
}
